import gsap from 'gsap'

let isInitialized = false
let idleTimeline: gsap.core.Timeline | null = null
let introTimeline: gsap.core.Timeline | null = null
let balloonTimeline: gsap.core.Timeline | null = null
let balloonFlyAwayTimeout: ReturnType<typeof setTimeout> | null = null
let textRotationTimeout: ReturnType<typeof setTimeout> | null = null

const headingTexts = [
  'You and me on purpose always',
  "I'm not patient. I'm just certain",
  'I love you',
  'Made with love',
]
let currentTextIndex = 0

function getSceneParts() {
  const scene = document.getElementById('finale-scene')
  if (!scene) return null

  const heading = document.querySelector('.finale__heading') as HTMLElement | null
  const mountains = scene.querySelector('[data-finale-mountains]') as HTMLElement | null
  const terrain = scene.querySelector('[data-finale-terrain]') as HTMLElement | null
  const clouds = Array.from(scene.querySelectorAll('[data-finale-cloud]')) as HTMLElement[]
  const flowers = Array.from(scene.querySelectorAll('[data-finale-flower]')) as HTMLElement[]
  const flowerSvgs = flowers
    .map((flower) => flower.querySelector('svg') as SVGElement | null)
    .filter((svg): svg is SVGElement => Boolean(svg))
  const balloon = scene.querySelector('[data-finale-balloon]') as HTMLElement | null

  if (!heading || !mountains || !terrain || clouds.length === 0 || flowers.length === 0) return null
  return { heading, mountains, terrain, clouds, flowers, flowerSvgs, balloon }
}

function setIntroState(): void {
  const parts = getSceneParts()
  if (!parts) return

  // Reset text rotation
  if (textRotationTimeout) {
    clearTimeout(textRotationTimeout)
    textRotationTimeout = null
  }
  currentTextIndex = 0
  parts.heading.textContent = headingTexts[0]

  gsap.set(parts.heading, { opacity: 0, y: 28 })
  // Mountains and terrain are instant - no animation needed
  gsap.set(parts.mountains, { opacity: 1, y: 0 })
  gsap.set(parts.terrain, { opacity: 1, y: 0 })
  gsap.set(parts.clouds, { opacity: 0 })
  gsap.set(parts.clouds[0], { x: -160 })
  gsap.set(parts.clouds[1], { x: -95 })
  gsap.set(parts.clouds[2], { x: 110 })
  gsap.set(parts.clouds[3], { x: 170 })
  gsap.set(parts.flowers, { opacity: 0, y: 14 })
  gsap.set(parts.flowerSvgs, { scale: 0.22, transformOrigin: '50% 100%' })
  if (parts.balloon) {
    gsap.set(parts.balloon, { opacity: 0, scale: 0.5, y: 40 })
  }
}

function startBalloonFloat(balloon: HTMLElement): void {
  if (balloonTimeline) {
    balloonTimeline.kill()
    balloonTimeline = null
  }

  // Create a gentle floating animation with slight rotation
  balloonTimeline = gsap.timeline({ repeat: -1, yoyo: true })

  // Gentle up/down bobbing with slight left/right sway
  balloonTimeline.to(balloon, {
    y: '-=12',
    x: '+=8',
    rotation: 3,
    duration: 2.5,
    ease: 'sine.inOut',
  }, 0)

  balloonTimeline.to(balloon, {
    y: '-=8',
    x: '-=12',
    rotation: -4,
    duration: 3.2,
    ease: 'sine.inOut',
  }, 2.5)

  balloonTimeline.to(balloon, {
    y: '+=6',
    x: '+=6',
    rotation: 2,
    duration: 2.8,
    ease: 'sine.inOut',
  }, 5.7)
}

function flyBalloonAway(balloon: HTMLElement): void {
  if (balloonTimeline) {
    balloonTimeline.kill()
    balloonTimeline = null
  }

  // Fly away animation - drifts up and slightly to the side
  gsap.to(balloon, {
    y: -window.innerHeight - 200,
    x: '+=80',
    rotation: 15,
    duration: 8,
    ease: 'power1.in',
  })
}

function rotateHeadingText(heading: HTMLElement): void {
  // Clear any existing timeout
  if (textRotationTimeout) {
    clearTimeout(textRotationTimeout)
    textRotationTimeout = null
  }

  // Don't rotate if we're on the last text ("Made with love")
  if (currentTextIndex >= headingTexts.length - 1) {
    return
  }

  // Schedule the next text rotation
  textRotationTimeout = setTimeout(() => {
    // Get the next text
    currentTextIndex = currentTextIndex + 1
    const newText = headingTexts[currentTextIndex]

    // Animate out, change text, animate in
    gsap.timeline()
      .to(heading, {
        opacity: 0,
        y: -20,
        duration: 0.4,
        ease: 'power2.in',
      })
      .call(() => {
        heading.textContent = newText
      })
      .to(heading, {
        opacity: 1,
        y: -130,
        duration: 0.5,
        ease: 'power2.out',
      })
      .call(() => {
        // Schedule the next rotation (if not the last text)
        rotateHeadingText(heading)
      })
  }, 3000)
}

function startIdleScene(): void {
  const parts = getSceneParts()
  if (!parts) return

  if (idleTimeline) {
    idleTimeline.kill()
    idleTimeline = null
  }

  idleTimeline = gsap.timeline({ repeat: -1, yoyo: true, defaults: { ease: 'sine.inOut' } })

  idleTimeline.to(parts.mountains, { y: -5, duration: 9.5 }, 0)
  idleTimeline.to(parts.terrain, { y: -2, duration: 12 }, 0)
  // Clouds move more noticeably
  idleTimeline.to(parts.clouds[0], { x: '-=35', y: -6, duration: 14 }, 0)
  idleTimeline.to(parts.clouds[1], { x: '+=30', y: -4, duration: 10 }, 0)
  idleTimeline.to(parts.clouds[2], { x: '-=28', y: -7, duration: 12 }, 0)
  idleTimeline.to(parts.clouds[3], { x: '+=40', y: -5, duration: 8.5 }, 0)
  idleTimeline.to(parts.flowers, { y: '-=2', duration: 5.8, stagger: { each: 0.22, from: 'random' } }, 0)
}

export function initFinaleScene(): void {
  if (isInitialized) return
  const parts = getSceneParts()
  if (!parts) return

  setIntroState()
  isInitialized = true
}

export function playFinaleScene(onComplete?: () => void): void {
  initFinaleScene()
  const parts = getSceneParts()
  if (!parts) {
    onComplete?.()
    return
  }

  if (introTimeline) {
    introTimeline.kill()
    introTimeline = null
  }
  if (idleTimeline) {
    idleTimeline.kill()
    idleTimeline = null
  }

  setIntroState()

  introTimeline = gsap.timeline({
    onComplete: () => {
      startIdleScene()
      // Start rotating the heading text after 3 seconds
      rotateHeadingText(parts.heading)
      onComplete?.()
    },
  })

  introTimeline.to(parts.heading, { opacity: 1, y: -130, duration: 0.7, ease: 'power2.out' }, 0)
  // Mountains and terrain are already visible, no animation needed
  introTimeline.to(parts.clouds[0], { x: 0, opacity: 1, duration: 1.05, ease: 'power2.out' }, 0.18)
  introTimeline.to(parts.clouds[1], { x: 0, opacity: 1, duration: 0.9, ease: 'power2.out' }, 0.28)
  introTimeline.to(parts.clouds[2], { x: 0, opacity: 1, duration: 0.95, ease: 'power2.out' }, 0.35)
  introTimeline.to(parts.clouds[3], { x: 0, opacity: 1, duration: 1.12, ease: 'power2.out' }, 0.45)
  introTimeline.to(parts.flowers, { opacity: 1, y: 0, duration: 0.22, stagger: 0.08, ease: 'power1.out' }, 0.7)
  introTimeline.to(parts.flowerSvgs, { scale: 1, duration: 0.44, stagger: 0.08, ease: 'back.out(2.4)' }, 0.7)

  // Balloon appears and starts floating
  if (parts.balloon) {
    introTimeline.to(parts.balloon, {
      opacity: 1,
      scale: 1,
      y: 0,
      duration: 0.8,
      ease: 'back.out(1.5)',
      onComplete: () => {
        if (parts.balloon) {
          startBalloonFloat(parts.balloon)

          // After 10 seconds, fly away!
          if (balloonFlyAwayTimeout) clearTimeout(balloonFlyAwayTimeout)
          balloonFlyAwayTimeout = setTimeout(() => {
            if (parts.balloon) flyBalloonAway(parts.balloon)
          }, 9500)
        }
      },
    }, 0.9)
  }
}
